// The reader (web/templates/web/_reader.html): a book full screen with real
// page turns. Used by the book page (book.js hands in the live book) and
// READ-ONLY by the public example books (a JSON in the page).
//
// How it turns: the book is a stack of LEAVES lying on the right half. A leaf
// has a front (a right-hand page) and a back (the next left-hand page) and
// turns over the spine - rotateY(-180deg) around its left edge, both faces
// with backface-visibility hidden. Turned leaves lie on the left half. On a
// phone (narrow or portrait screen) every page is a leaf of its own and turns
// away to the left. prefers-reduced-motion: no animation, the page changes.
//
// Turning is PREDICTABLE (Dennis, 2026-10-15): the right half of the screen turns forward, the left half back -
// on the book AND in the empty space beside it (a big arrow on each side lights up and the cursor shows the
// direction); the closed cover opens with a click anywhere on it, the closed back cover turns back. Keys
// (arrows, PageUp/Down, space, Home/End), a swipe on touch screens and a two-finger swipe on a trackpad
// (horizontal wheel, one gesture = one page) turn as well, and so does a DRAG with the mouse / a click-drag on
// the trackpad: the direction comes ONLY from the movement (left = forward, right = back), never from where it is
// released; a pointer that moved more than 8 px is a drag and never also a click (the click is swallowed).
// Pictures are never selected or dragged as images.
//
// Phones (one page per screen): the picture and the text of a spread are ONE double page - between them the
// pair SLIDES sideways (following the finger, snapping), only from a spread's right page to the next spread
// (and from/to single pages: cover, title, closing page, back) the page turns. Pairs are found generically:
// faces 2k-1 and 2k of the reading order are the left and right page of the k-th desktop spread.
//
// The JSON contract (see tools/COORDINATION.md):
//   {title, dedication, hero, format: 'square'|'a4-portrait'|'a4-landscape', ratio: '1 / 1',
//    font: 'nunito'|'andika'|'grandstander'|'literata', text_size: 's'|'m'|'l',
//    cover: url|null, spreads: [{picture: url|null, text}], back_text,
//    foreword, foreword_by, closing, has_audio,
//    figures: [{url, name} of each painted figure, hero first]}   (the last five optional; a figure may be a plain url)
//
//   window.HeldenReader.open(data, {at: 0})   open (at = leaf position, 0 = closed cover)
//   window.HeldenReader.refresh(data)         new data while open (keeps the position)
//   window.HeldenReader.goTo(at)              jump to a leaf position (no animation; used by the screenshot scripts)
//   window.HeldenReader.close()
//   Any element with [data-reader-open] opens the JSON of <script id="reader-data">
//   (or the id in data-reader-open="other-id").
const {closingPage, coverFace, backFace, endpaper, esc, fitText, folio, forewordPage, heroesPage, pictureImg, textPage, titlePage} = require('./bookpages');

const el = document.querySelector('[data-reader]');
let data = null;
let pos = 0;                  // how many leaves are turned
let leaves = [];
let single = false;
let pairs = [];               // phones: pairs[i] = true when leaf i and leaf i + 1 are the two pages of one spread
let lastFocus = null;

const ARROW = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const ratioOf = (r) => {
  const [w, h] = String(r || '1 / 1').split('/').map(Number);
  return w && h ? w / h : 1;
};

// every face of the book in reading order (the same 2n + 4 inner pages as the PDF, between the covers).
// Page 1 is a right-hand page and p32 a left-hand one: on a spread the inside of the cover faces them
// (`endpaper`), on a phone (one page per leaf) the endpapers are left out.
function faces(book, single) {
  const blank = (label) => ({kind: 'blank', html: endpaper(label === 'front' ? 'is-left' : 'is-right'), label: ''});
  const list = [{kind: 'cover', html: `<div class="rd-cover-face">${coverFace(book, book.cover)}</div>`, label: 'Titelbild'}];
  if (!single) list.push(blank('front'));
  list.push({kind: 'title', html: titlePage(book), label: 'Titelseite'});
  list.push({kind: 'foreword', html: forewordPage(book, {side: 'is-left'}), label: 'Vorwort'});
  list.push({kind: 'heroes', html: heroesPage(book), label: 'Unsere Helden'});
  book.spreads.forEach((s, i) => {
    const k = i + 1;
    list.push({kind: 'picture', html: `<div class="bk-page bk-pic is-left">${pictureImg(s.picture)}</div>`, label: `Seite ${k}`});
    list.push({kind: 'text', html: textPage(s.text, folio.text(k), {font: book.font, size: book.text_size}), label: `Seite ${k}`});
  });
  list.push({kind: 'closing', html: closingPage(book), label: 'Ende'});
  if (!single) list.push(blank('back'));
  list.push({kind: 'back', html: `<div class="rd-cover-face">${backFace(book)}</div>`, label: 'Rückseite'});
  return list;
}

function build() {
  single = window.innerWidth < 760 || window.innerWidth / window.innerHeight < 0.85 || data.format === 'a4-landscape';
  el.classList.toggle('is-single', single);
  el.dataset.font = data.font || 'nunito';
  el.dataset.textSize = data.text_size || 'm';
  el.dataset.format = data.format || 'square';
  el.style.setProperty('--ratio', data.ratio || '1 / 1');
  el.querySelector('[data-rd-title]').textContent = data.title || '';

  const list = faces(data, single);
  leaves = [];
  pairs = [];
  if (single) {
    // a blank page (endpaper) has nothing to read on a phone; the face's index in the reading order says where it
    // stands in the printed spread: odd = left page, even = right page (0 = cover)
    const lone = (f) => !f || ['cover', 'back', 'blank'].includes(f.kind) || /\bis-blank\b/.test(f.html);
    list.forEach((f, idx) => {
      if (f.kind === 'blank' || (f.kind !== 'cover' && f.kind !== 'back' && /\bis-blank\b/.test(f.html))) return;
      leaves.push({front: f, back: null, idx});
    });
    leaves.forEach((leaf, i) => {
      const next = leaves[i + 1];
      pairs[i] = !!next && leaf.idx % 2 === 1 && next.idx === leaf.idx + 1 && !lone(leaf.front) && !lone(next.front);
    });
  } else {
    for (let i = 0; i < list.length; i += 2) leaves.push({front: list[i], back: list[i + 1]});
  }
  const stage = el.querySelector('[data-rd-stage]');
  stage.innerHTML = `<div class="rd-book" data-rd-book><span class="rd-under"></span>${leaves.map((leaf, i) => `
    <div class="rd-leaf" data-leaf="${i}">
      <div class="rd-face is-front${leaf.front.kind === 'cover' ? ' is-cover' : ''}">${leaf.front.html}<span class="rd-shade"></span></div>
      <div class="rd-face is-back${leaf.back && leaf.back.kind === 'back' ? ' is-cover' : ''}">${leaf.back ? leaf.back.html : ''}</div>
    </div>`).join('')}</div>
    <button type="button" class="rd-side is-prev" data-rd-prev aria-label="Zurückblättern">${ARROW}</button>
    <button type="button" class="rd-side is-next" data-rd-next aria-label="Weiterblättern">${ARROW}</button>`;
  stage.querySelectorAll('img').forEach(img => { img.draggable = false; });
  pos = Math.min(pos, maxPos());
  size();
  place(false);
  fitText(stage, data.font, data.text_size);
  if (single) fitSingle(stage);
}

// Phones: every leaf has the book's own shape (a square book = square leaves, centred - Dennis 2026-10-15: a
// portrait leaf next to a small square picture looked "sehr komisch"). A text page that does not fit first gets a
// little smaller letters (down to 0.82), then grows taller - only as much as it needs, up to the free height of the
// screen -, then shrinks further (min 0.66), and only as the very last resort it scrolls with a fade. Never cut
// (QA: 8 of 12 texts ran over a square page; samples/book/reader-fit.js checks every font x size).
function fitSingle(stage) {
  const full = Math.floor(stage.clientHeight * 0.96);
  const pw = parseFloat(stage.querySelector('[data-rd-book]').style.getPropertyValue('--pw'));
  const pageH = Math.floor(pw / ratioOf(data.ratio));
  stage.querySelectorAll('[data-leaf]').forEach(leaf => {
    leaf.style.height = `${pageH}px`;
    leaf.style.top = '0px';
    const el = leaf.querySelector('.is-front .bk-text');
    const body = el && el.querySelector('.bk-text-body');
    if (!body) return;
    const style = getComputedStyle(el);
    const pad = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const ornament = el.querySelector('.bk-ornament');
    const extra = ornament ? ornament.offsetHeight + parseFloat(getComputedStyle(ornament).marginBottom || 0) : 0;
    const need = () => body.offsetHeight + extra;
    const over = () => need() > el.clientHeight - pad;
    let fit = 1;
    const shrinkTo = (min) => {
      while (over() && fit > min) {
        fit = Math.round((fit - 0.04) * 100) / 100;
        el.style.setProperty('--sfit', fit);
      }
    };
    el.style.setProperty('--sfit', fit);
    shrinkTo(0.82);
    if (over()) {
      const h = Math.min(full, Math.ceil(need() + pad) + 2);
      leaf.style.height = `${h}px`;
      leaf.style.top = `${Math.floor((pageH - h) / 2)}px`;   // grows evenly above and below the book's centre
      shrinkTo(0.66);
    }
    el.classList.toggle('is-scroll', over());
  });
}

const maxPos = () => (single ? leaves.length - 1 : leaves.length);

function size() {
  const stage = el.querySelector('[data-rd-stage]');
  const r = ratioOf(data.ratio);
  const w = stage.clientWidth;
  const h = stage.clientHeight;
  // desktop: room beside the book for the two big arrows (and the empty space there turns too)
  const side = Math.max(64, Math.min(110, w * 0.07));
  const pw = single ? Math.min(w * 0.94, h * 0.96 * r) : Math.min((w - 2 * side) / 2, h * 0.94 * r);
  const book = stage.querySelector('[data-rd-book]');
  book.style.setProperty('--pw', `${Math.floor(pw)}px`);
  stage.style.setProperty('--pw', `${Math.floor(pw)}px`);   // the side arrows sit beside the book
  book.style.setProperty('--r', r);
}

function place(animate = true, moving = -1, mode = 'turn') {
  const book = el.querySelector('[data-rd-book]');
  const n = leaves.length;
  book.classList.toggle('is-closed-front', !single && pos === 0);
  book.classList.toggle('is-closed-back', !single && pos === n);
  if (single) placeSingle(animate, moving, mode);
  else {
    el.querySelectorAll('[data-leaf]').forEach(leaf => {
      const i = Number(leaf.dataset.leaf);
      const turned = i < pos;
      leaf.classList.toggle('is-turned', turned);
      // the turned stack: the last one on top; the unturned stack: the next one on top; the moving leaf above all
      leaf.style.zIndex = i === moving ? n + 5 : (turned ? i + 1 : n - i);
      leaf.setAttribute('aria-hidden', String(!(i === pos || i === pos - 1)));
    });
  }
  if (moving >= 0 && animate && mode === 'turn') {
    const leaf = el.querySelector(`[data-leaf="${moving}"]`);
    leaf.classList.add('is-moving');
    clearTimeout(place.t);
    place.t = setTimeout(() => {
      leaf.classList.remove('is-moving');
      place(false);
    }, 950);
  }
  // what the visitor sees, in printed page numbers
  let label;
  if (single) label = leaves[pos].front.label;
  else if (pos === 0) label = 'Titelbild';
  else if (pos === n) label = 'Rückseite';
  else label = [...new Set([leaves[pos - 1].back, leaves[pos].front].map(f => f && f.label).filter(Boolean))].join(' · ');
  el.querySelector('[data-rd-count]').textContent = label;
  el.querySelectorAll('[data-rd-prev]').forEach(b => { b.disabled = pos === 0; });
  el.querySelectorAll('[data-rd-next]').forEach(b => { b.disabled = pos === maxPos(); });
}

// Phones. Every leaf is in one state: the current page; the other page of the current spread lying beside it
// (slid-left / slid-right, invisible at rest, it comes in sideways); turned away; or still ahead (hidden).
// Only the leaves of the move animate - everything else jumps to its state without a transition.
const STATES = ['is-turned', 'is-slid-left', 'is-slid-right'];
function placeSingle(animate, moving, mode) {
  const n = leaves.length;
  const anim = new Set();
  if (animate && moving >= 0) {
    if (mode === 'slide') { anim.add(moving); anim.add(moving + 1); } else anim.add(moving);
  }
  // while a page turns BACK over the current one, the current one stays where it is until the turn is done
  const covered = animate && mode === 'turn' && moving === pos ? moving + 1 : -1;
  const all = [...el.querySelectorAll('[data-leaf]')];
  all.forEach(leaf => {
    const i = Number(leaf.dataset.leaf);
    let state = '';
    if (i === pos || i === covered) state = '';
    else if (i === pos - 1 && pairs[i]) state = 'is-slid-left';
    else if (i === pos + 1 && pairs[pos]) state = 'is-slid-right';
    else if (i < pos) state = 'is-turned';
    else state = 'ahead';
    const visible = state !== 'ahead' && !(state === 'is-turned' && !anim.has(i));
    leaf.classList.toggle('rd-snap', !anim.has(i));
    leaf.classList.toggle('is-sliding', anim.has(i) && mode === 'slide');
    STATES.forEach(c => leaf.classList.toggle(c, c === state));
    leaf.style.visibility = visible ? '' : 'hidden';
    leaf.style.zIndex = anim.has(i) && mode === 'turn' ? n + 5 : (i === pos ? n + 2 : n - i);
    leaf.setAttribute('aria-hidden', String(i !== pos));
  });
  void el.offsetWidth;                         // apply the jumps without a transition, then allow them again
  all.forEach(leaf => leaf.classList.remove('rd-snap'));
  if (mode === 'slide' && animate) {
    clearTimeout(placeSingle.t);
    placeSingle.t = setTimeout(() => all.forEach(leaf => leaf.classList.remove('is-sliding')), 500);
  }
}

function go(delta) {
  const to = Math.max(0, Math.min(maxPos(), pos + delta));
  if (to === pos) return;
  const moving = delta > 0 ? pos : to;          // forward: the leaf at pos turns; back: the last turned one
  // phones: inside one spread (picture <-> its text) the pair slides; everything else turns
  const mode = single && Math.abs(to - pos) === 1 && pairs[Math.min(pos, to)] ? 'slide' : 'turn';
  pos = to;
  place(true, moving, mode);
}

// which way a click / tap at clientX turns: the right half forward, the left half back; a closed book opens
function dirAt(x) {
  if (pos === 0) return 1;
  if (pos === maxPos()) return -1;
  const r = el.querySelector('[data-rd-stage]').getBoundingClientRect();
  return x >= r.left + r.width / 2 ? 1 : -1;
}

function goTo(at) {
  pos = Math.max(0, Math.min(maxPos(), at));
  place(false);
}

function open(book, {at = 0} = {}) {
  data = book;
  pos = at;
  lastFocus = document.activeElement;
  el.hidden = false;
  document.documentElement.style.overflow = 'hidden';
  document.documentElement.style.overscrollBehaviorX = 'none';   // a trackpad swipe must not go back in history
  build();
  el.querySelector('[data-rd-close]').focus({preventScroll: true});
}

function refresh(book) {
  if (el.hidden) return;
  data = book;
  build();
}

function close() {
  el.hidden = true;
  document.documentElement.style.overflow = '';
  document.documentElement.style.overscrollBehaviorX = '';
  delete el.dataset.hover;
  if (lastFocus && lastFocus.focus) lastFocus.focus({preventScroll: true});
}

const drag = {on: false, x: 0, y: 0, dx: 0, axis: '', swallowUntil: 0, downAt: -1, downTime: 0};

if (el) {
  // a drag is never also a click: the click that follows a drag is swallowed before anything sees it
  el.addEventListener('click', (e) => {
    if (Date.now() < drag.swallowUntil) { e.stopPropagation(); e.preventDefault(); drag.swallowUntil = 0; }
  }, true);
  el.addEventListener('click', (e) => {
    if (e.target.closest('[data-rd-prev]')) go(-1);
    else if (e.target.closest('[data-rd-next]')) go(1);
    else if (e.target.closest('[data-rd-close]')) close();
    // a real click: the half where it was PRESSED decides (the release point never does)
    else if (e.target.closest('[data-rd-stage]')) go(dirAt(drag.downAt >= 0 && Date.now() - drag.downTime < 1500 ? drag.downAt : e.clientX));
  });
  // the cue: the arrow of the side the pointer is on lights up, the cursor points the way
  el.addEventListener('mousemove', (e) => {
    const onStage = e.target.closest('[data-rd-stage]');
    const d = onStage ? dirAt(e.clientX) : 0;
    const can = d > 0 ? pos < maxPos() : d < 0 ? pos > 0 : false;
    const hover = can ? (d > 0 ? 'next' : 'prev') : '';
    if ((el.dataset.hover || '') !== hover) {
      if (hover) el.dataset.hover = hover;
      else delete el.dataset.hover;
    }
  });
  el.addEventListener('mouseleave', () => { delete el.dataset.hover; });
  // trackpad: a two-finger swipe sends horizontal wheel events (and keeps sending them while it glides out).
  // One gesture = one page: the deltas add up until 50 px, then everything is ignored until the events have
  // paused for 220 ms (the end of the glide) - but at least 450 ms. Mostly vertical scrolling is left alone.
  const wheel = {sum: 0, locked: false, until: 0, timer: null};
  el.addEventListener('wheel', (e) => {
    if (e.ctrlKey) return;                                     // pinch zoom
    const dx = e.deltaX * (e.deltaMode === 1 ? 16 : 1);
    if (Math.abs(dx) <= Math.abs(e.deltaY) * 1.2) return;      // vertical (a scrolling text page keeps it)
    e.preventDefault();
    clearTimeout(wheel.timer);
    wheel.timer = setTimeout(() => { wheel.locked = false; wheel.sum = 0; }, Math.max(220, wheel.until - Date.now()));
    if (wheel.locked) return;
    wheel.sum += dx;
    if (Math.abs(wheel.sum) >= 50) {
      go(wheel.sum > 0 ? 1 : -1);
      wheel.locked = true;
      wheel.until = Date.now() + 450;
      wheel.sum = 0;
    }
  }, {passive: false});
  document.addEventListener('keydown', (e) => {
    if (el.hidden) return;
    if (['ArrowRight', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); go(1); }
    if (['ArrowLeft', 'PageUp'].includes(e.key)) { e.preventDefault(); go(-1); }
    if (e.key === 'Home') go(-pos);
    if (e.key === 'End') go(maxPos() - pos);
    if (e.key === 'Escape') close();
  });
  // Dragging - finger (touch events) and mouse / trackpad click-drag (pointer events) share one gesture:
  // more than 8 px = a drag; it is horizontal when it moves more sideways than up/down (a scrolling text page
  // keeps the vertical one). On a phone the pair of a spread follows the finger; release past 50 px (or 18 %
  // of the page) turns / slides that way, otherwise it springs back.
  el.addEventListener('dragstart', (e) => e.preventDefault());
  const startDrag = (x, y) => {
    drag.on = true; drag.x = x; drag.y = y; drag.dx = 0; drag.axis = '';
    drag.downAt = x; drag.downTime = Date.now();
  };
  const dragLeaves = () => {
    const cur = el.querySelector(`[data-leaf="${pos}"]`);
    const prev = drag.dx > 0 && pairs[pos - 1] ? el.querySelector(`[data-leaf="${pos - 1}"]`) : null;
    const next = drag.dx < 0 && pairs[pos] ? el.querySelector(`[data-leaf="${pos + 1}"]`) : null;
    return {cur, other: prev || next, offset: prev ? '-100% - 16px' : '100% + 16px'};
  };
  const clearDrag = () => {
    el.querySelectorAll('[data-leaf].is-dragging').forEach(l => {
      l.classList.remove('is-dragging');
      l.style.transform = '';
      l.style.opacity = '';
    });
  };
  const moveDrag = (x, y) => {
    if (!drag.on) return;
    drag.dx = x - drag.x;
    const dy = y - drag.y;
    if (!drag.axis && Math.hypot(drag.dx, dy) > 8) {
      drag.axis = Math.abs(drag.dx) > Math.abs(dy) ? 'x' : 'y';
      drag.swallowUntil = Number.MAX_SAFE_INTEGER;            // moved: whatever follows is not a click
    }
    if (drag.axis !== 'x' || !single) return;
    clearDrag();
    const {cur, other, offset} = dragLeaves();
    if (!cur) return;
    // into the other page of the spread the pair moves 1:1; towards a page turn only a little (it then turns)
    const dx = other ? drag.dx : drag.dx * 0.25;
    cur.classList.add('is-dragging');
    cur.style.transform = `translateX(${dx}px)`;
    if (other) {
      other.classList.add('is-dragging');
      other.style.transform = `translateX(calc(${offset} + ${dx}px))`;
      other.style.opacity = '1';
      other.style.visibility = '';
    }
  };
  const endDrag = () => {
    if (!drag.on) return;
    drag.on = false;
    const moved = drag.axis !== '';
    if (moved) drag.swallowUntil = Date.now() + 400;
    const page = el.querySelector('[data-rd-book]');
    const width = page ? page.getBoundingClientRect().width / (single ? 1 : 2) : 300;
    const dragged = [...el.querySelectorAll('[data-leaf].is-dragging')];
    if (drag.axis === 'x' && Math.abs(drag.dx) >= Math.min(50, width * 0.18)) {
      clearDrag();                                // the leaves move on from where the finger left them
      go(drag.dx < 0 ? 1 : -1);
    } else if (dragged.length) {                  // not far enough: the pages spring back
      dragged.forEach(l => l.classList.add('is-sliding'));
      clearDrag();
      setTimeout(() => dragged.forEach(l => l.classList.remove('is-sliding')), 500);
    }
  };
  el.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1 && e.target.closest('[data-rd-stage]')) startDrag(e.touches[0].clientX, e.touches[0].clientY);
  }, {passive: true});
  el.addEventListener('touchmove', (e) => { if (drag.on) moveDrag(e.touches[0].clientX, e.touches[0].clientY); }, {passive: true});
  el.addEventListener('touchend', endDrag);
  el.addEventListener('touchcancel', endDrag);
  el.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch' || e.button !== 0 || !e.target.closest('[data-rd-stage]') || e.target.closest('button')) return;
    startDrag(e.clientX, e.clientY);
  });
  window.addEventListener('pointermove', (e) => { if (e.pointerType !== 'touch' && drag.on) moveDrag(e.clientX, e.clientY); });
  window.addEventListener('pointerup', (e) => { if (e.pointerType !== 'touch') endDrag(); });
  window.addEventListener('pointercancel', (e) => { if (e.pointerType !== 'touch') endDrag(); });
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (el.hidden) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(build, 150);
  });
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-reader-open]');
    if (!opener) return;
    const script = document.getElementById(opener.dataset.readerOpen || 'reader-data');
    if (!script) return;
    e.preventDefault();
    open(JSON.parse(script.textContent));
  });
}

window.HeldenReader = {open, refresh, close, goTo, isOpen: () => el && !el.hidden, esc};
